import { api } from "./client";
import type { components } from "./contract";
import { unwrap } from "./errors";

/** A finalized object-storage file, as returned by the assets upload flow. */
export type UploadedFile = components["schemas"]["File"];

/**
 * The reusable 3-step direct-upload flow shared by every studio (image
 * references, the video reference frame):
 *   1. POST /v1/assets/files/upload-init  → { file, upload_url }
 *   2. PUT the bytes straight to object storage at `upload_url`
 *   3. POST /v1/assets/files/complete     → finalized { file }
 *
 * Lives in the `api` layer (not `shared`) because it talks to the client; the
 * presentational `FileUploader` widget takes it as an injected `onUpload`.
 */
export async function uploadFile(file: File): Promise<UploadedFile> {
  const init = unwrap(
    await api.POST("/v1/assets/files/upload-init", {
      body: { file_name: file.name, mime_type: file.type, size_bytes: file.size },
    }),
  );

  const put = await fetch(init.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  const done = unwrap(
    await api.POST("/v1/assets/files/complete", { body: { file_id: init.file.id } }),
  );
  return done.file;
}
